<!-- .../builder/canvas/InlineTextEditor.vue -->
<script setup>
// Editor de texto dentro do canvas (duplo clique no texto/título). Emite o
// HTML a cada mudança; Esc ou "Pronto" termina. Título = modo inline.
import { ref, shallowRef, onMounted, onBeforeUnmount } from 'vue';
import { EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { history, undo, redo } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import { baseKeymap, toggleMark } from 'prosemirror-commands';
import {
  wrapInList,
  liftListItem,
  splitListItem,
} from 'prosemirror-schema-list';
import 'prosemirror-view/style/prosemirror.css';
import { buildSchema, parseHtml, serializeHtml, SAFE_URL } from './richSchema';
import TextToolbar from './TextToolbar.vue';
import { normalizeHref } from '../document/links';

const props = defineProps({
  html: { type: String, default: '' },
  inline: { type: Boolean, default: false },
  styleObject: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update', 'done']);
const host = ref(null);
const view = shallowRef(null);
const schema = buildSchema({ inline: props.inline });

const emitHtml = () =>
  emit('update', serializeHtml(schema, view.value.state.doc));

const run = command => {
  if (!view.value) return;
  command(view.value.state, view.value.dispatch);
  view.value.focus();
};

const setTextStyle = attrs => (state, dispatch) => {
  const type = schema.marks.textStyle;
  const { from, to, empty } = state.selection;
  if (empty) return false;
  let current = {};
  state.doc.nodesBetween(from, to, node => {
    const found = node.marks.find(m => m.type === type);
    if (found) current = found.attrs;
  });
  dispatch(state.tr.addMark(from, to, type.create({ ...current, ...attrs })));
  return true;
};

const setLink = raw => (state, dispatch) => {
  const type = schema.marks.link;
  const { from, to, empty } = state.selection;
  if (empty) return false;
  const href = normalizeHref(raw);
  if (href && !SAFE_URL.test(href)) return false;
  const tr = state.tr.removeMark(from, to, type);
  if (href) tr.addMark(from, to, type.create({ href }));
  dispatch(tr);
  return true;
};

const commands = {
  bold: () => run(toggleMark(schema.marks.strong)),
  italic: () => run(toggleMark(schema.marks.em)),
  underline: () => run(toggleMark(schema.marks.underline)),
  link: href => run(setLink(href)),
  color: hex => run(setTextStyle({ color: hex })),
  size: px => run(setTextStyle({ size: px })),
  list: () => !props.inline && run(wrapInList(schema.nodes.bullet_list)),
  ordered: () => !props.inline && run(wrapInList(schema.nodes.ordered_list)),
  variable: name =>
    run((state, dispatch) => {
      dispatch(state.tr.insertText(`{{${name}}}`));
      return true;
    }),
};
defineExpose({ commands, view });

onMounted(() => {
  const done = () => {
    emit('done');
    return true;
  };
  const listKeys = props.inline
    ? { Enter: done }
    : {
        Enter: splitListItem(schema.nodes.list_item),
        'Shift-Tab': liftListItem(schema.nodes.list_item),
      };
  const state = EditorState.create({
    doc: parseHtml(schema, props.html),
    plugins: [
      history(),
      keymap({
        'Mod-z': undo,
        'Mod-y': redo,
        'Mod-Shift-z': redo,
        'Mod-b': toggleMark(schema.marks.strong),
        'Mod-i': toggleMark(schema.marks.em),
        'Mod-u': toggleMark(schema.marks.underline),
        Escape: done,
        ...listKeys,
      }),
      keymap(baseKeymap),
    ],
  });
  view.value = new EditorView(host.value, {
    state,
    dispatchTransaction(tr) {
      view.value.updateState(view.value.state.apply(tr));
      if (tr.docChanged) emitHtml();
    },
  });
  view.value.focus();
});
onBeforeUnmount(() => view.value?.destroy());
</script>

<template>
  <div class="relative" @click.stop @dblclick.stop>
    <TextToolbar
      class="absolute bottom-full left-0 z-30 mb-2"
      :inline="inline"
      @command="(name, arg) => commands[name]?.(arg)"
      @done="emit('done')"
    />
    <div
      ref="host"
      class="cursor-text [&_.ProseMirror]:outline-none"
      :style="styleObject"
    />
  </div>
</template>
